import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { ErrorMessages } from "../constants/constants";
import { ApiErrorInfoModel } from "../models/service/error-info.model";
import { ServiceResultModel } from "../models/service/service-result.model";
import { PaginationResponseHeader } from "../models/service/pagination-response-header.model";
import { ApiResponseUtilities } from "./api-response.utilities";
import { CommonChecks } from "./common-checks.utilities";

export class ServiceUtilities {

    private static readonly PAGINATION_HEADER = "x-pagination";

    /**
     * The x-pagination header, or undefined when the endpoint did not send one.
     *
     * Most list endpoints parse this header inline and assert it is present, which turns a missing or
     * malformed header into a failed load rather than an unpaginated one. An endpoint whose paging is
     * optional needs to tell the two apart, so it reads the header through here and supplies its own
     * fallbacks.
     */
    public static parsePaginationHeader<T>(response: HttpResponse<T>): PaginationResponseHeader | undefined {
        const rawHeader = response.headers.get(ServiceUtilities.PAGINATION_HEADER);
        if (!rawHeader) return undefined;

        try {
            return <PaginationResponseHeader>JSON.parse(rawHeader);
        } catch {
            return undefined;
        }
    }

    public static handleAPIErrorResponse<T>(response: HttpResponse<T>): ServiceResultModel {
        const errorResponse = <ApiErrorInfoModel>response.body?.valueOf();
        let message = "";

        if (!errorResponse || CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(errorResponse.detail)) {
            message = ErrorMessages.DEFAULT_ERROR_MESSAGE();
        } else {
            message = errorResponse.detail;
        }
        return { success: false, message: message, statusCode: response.status };
    }

    public static handleServiceException(error: any, messageOverride: string | undefined = undefined): ServiceResultModel {
        console.error(error);

        if (error?.error?.statusCode) {
            return <ServiceResultModel>{
                success: false,
                message: (messageOverride) ? messageOverride : error.error.detail,
                statusCode: error.error.statusCode
            };
        }

        return <ServiceResultModel>{
            success: false,
            message: (messageOverride) ? messageOverride : ErrorMessages.DEFAULT_FAILURE_MESSAGE()
        };
    }

    // handleServiceException only surfaces a status code when the API returned a ProblemDetails body
    // carrying one, so a network failure or a body-less gateway 5xx comes back with statusCode
    // undefined - indistinguishable from a body-less 401/403. HttpErrorResponse.status always carries
    // the transport status (0 for a network/CORS failure), so callers that need to tell transient
    // apart from terminal (see retryTransient) backfill it from here.
    public static resolveStatusCode(error: any): number | undefined {
        if (error?.error?.statusCode) {
            return error.error.statusCode;
        }

        return (error instanceof HttpErrorResponse) ? error.status : undefined;
    }

    // A network error or any 5xx is treated as transient; everything else - including 401, 403 and
    // 404 - is terminal. Mirrors DocumentUploadManagerService.isTransient for the blob-PUT retry.
    // An undefined status means the throw wasn't an HttpErrorResponse at all (a genuine code bug),
    // which isn't worth stalling the caller over.
    private static isTransientStatusCode(statusCode: number | undefined): boolean {
        return statusCode === 0 || ApiResponseUtilities.isServerErrorStatusCode(statusCode);
    }

    private static delay(milliseconds: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }

    // Retries an operation that reports failure through its ServiceResultModel rather than by
    // throwing (the shape every service here returns), using linear backoff of baseDelayMs x attempt.
    // Returns the first successful result, or the last failure once retries are exhausted or a
    // terminal status is seen. A first-attempt success awaits nothing.
    public static async retryTransient<T>(
        operation: () => Promise<ServiceResultModel<T>>,
        maxRetries: number,
        baseDelayMs: number,
        logLabel: string
    ): Promise<ServiceResultModel<T>> {
        const totalAttempts = maxRetries + 1;
        let result: ServiceResultModel<T> = { success: false };

        for (let attempt = 1; attempt <= totalAttempts; attempt++) {
            result = await operation();

            if (result.success) {
                return result;
            }

            if (!ServiceUtilities.isTransientStatusCode(result.statusCode)) {
                console.warn(`${logLabel}: attempt ${attempt} of ${totalAttempts} failed with status ${result.statusCode} - not retryable, giving up.`);
                return result;
            }

            if (attempt === totalAttempts) {
                console.error(`${logLabel}: attempt ${attempt} of ${totalAttempts} failed with status ${result.statusCode} - retries exhausted, giving up.`);
                return result;
            }

            const delayMs = baseDelayMs * attempt;
            console.warn(`${logLabel}: attempt ${attempt} of ${totalAttempts} failed with status ${result.statusCode} - retrying in ${delayMs}ms.`);
            await ServiceUtilities.delay(delayMs);
        }

        return result;
    }

    public static isWindowAndLocalStorageUndefined(): boolean {
        return (typeof window === 'undefined' || typeof localStorage === 'undefined');
    }
}
